import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import API from "../../services/api";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { t } from "@lingui/macro";

const ReservationCard = ({ reservation, updateReservation, closeCard }) => {
  const { userEmail } = useAuth();
  const [isPaying, setIsPaying] = useState(false);
  const [cardAmount, setCardAmount] = useState(0);
  const [cashAmount, setCashAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [freeTickets, setFreeTickets] = useState([]);
  const [isGeneratingFreeTickets, setIsGeneratingFreeTickets] = useState(false);
  const navigate = useNavigate();

  const validateAmounts = () => {
    if (cardAmount + cashAmount === reservation.remainingAmount) {
      setErrorMessage("");
      return true;
    }
    setErrorMessage(t`The total payment must equal the remaining amount.`);
    return false;
  };

  const handlePaymentSubmit = async () => {
    if (!validateAmounts()) return;

    const paymentData = [];
    if (cardAmount > 0) paymentData.push({ paymentMethod: "card", amount: cardAmount });
    if (cashAmount > 0) paymentData.push({ paymentMethod: "cash", amount: cashAmount });

    const requestData = {
      reservationId: reservation.id,
      email: userEmail,
      payments: paymentData,
      purpose: "Reservation",
    };

    try {
      const response = await API.post("/api/reservations/pay-remaining-amount", requestData);
      if (response.status === 200) {
        alert(t`Payment successful!`);
        updateReservation(response.data);
      } else {
        alert(t`Error processing payment.`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(t`Error processing payment.`);
    }
  };

  const handleCardCheckboxChange = () => {
    setCardAmount(cardAmount === 0 ? reservation.remainingAmount : 0);
    setCashAmount(0);
  };

  const handleCashCheckboxChange = () => {
    setCashAmount(cashAmount === 0 ? reservation.remainingAmount : 0);
    setCardAmount(0);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await API.get(
        `/api/users/reservations/${reservation.id}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `reservation_${reservation.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert(t`Error downloading PDF.`);
    }
  };

  const handleCancelReservation = async () => {
    if (
      !window.confirm(
        t`Are you sure you want to cancel this reservation? Paid amount:` +
          " " +
          reservation.paidAmount +
          " MXN"
      )
    )
      return;

    try {
      const response = await API.post(
        `/api/reservations/cancel?reservationId=${reservation.id}`
      );
      if (response.status === 200) {
        alert(t`Reservation cancelled successfully!`);
        updateReservation(response.data);
      } else {
        alert(t`Error:` + " " + response.data);
      }
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      alert(t`Error cancelling reservation.`);
    }
  };

  const handleCloseCard = () => {
    if (closeCard) {
      closeCard();
    }
  };

  const handleCreateFreeTickets = async () => {
    setIsGeneratingFreeTickets(true);
    const requestData = {
      reservationId: reservation.id,
      ticketCount: reservation.totalCapacity,
    };

    try {
      const response = await API.post(`/api/tickets/generate-free-tickets`, requestData);
      if (response.status === 200) {
        const generatedTicketIds = response.data;

        const updatedReservation = {
          ...reservation,
          ticketIds: generatedTicketIds,
        };

        updateReservation(updatedReservation);
        alert(t`Free tickets generated successfully!`);
      } else {
        alert(t`Error generating free tickets.`);
      }
    } catch (error) {
      console.error("Error generating free tickets:", error);
      alert(t`Error generating free tickets.`);
    } finally {
      setIsGeneratingFreeTickets(false);
    }
  };

  const handleTicketDownload = (ticketId) => {
    API.get(`/api/users/tickets/${ticketId}/download`, { responseType: "blob" })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Download failed.");
        }
        return response.data;
      })
      .then((blob) => {
        const link = document.createElement("a");
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = `${ticketId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        alert(t`Error downloading file.`);
      });
  };

  return (
    <div className="reservation-card">
      <button className="close-btn" onClick={handleCloseCard}>×</button>

      <p><strong>{t`Email:`}</strong> {reservation.email}</p>
      <p><strong>{t`Phone Number:`}</strong> {reservation.phoneNumber}</p>
      <p><strong>{t`Start Date:`}</strong> {format(new Date(reservation.startDate), "dd MMM yyyy")}</p>
      <p><strong>{t`End Date:`}</strong> {format(new Date(reservation.endDate), "dd MMM yyyy")}</p>
      <p><strong>{t`Total Amount:`}</strong> {reservation.totalAmount}</p>
      <p><strong>{t`Paid Amount:`}</strong> {reservation.paidAmount}</p>
      <p><strong>{t`Remaining Amount:`}</strong> {reservation.remainingAmount}</p>
      <p><strong>{t`Payment Status:`}</strong> {reservation.paymentStatus}</p>

      <p>
        <strong>{t`Reservation Status:`}</strong> {reservation.status}
        {reservation.status === "CANCELED" && reservation.cancellationDate && (
          <> {t`at`} {format(new Date(reservation.cancellationDate), "dd MMM yyyy 'at' HH:mm")}</>
        )}
      </p>

      <p><strong>{t`Total Capacity:`}</strong> {reservation.totalCapacity}</p>

      <div>
        <strong>{t`Rooms:`}</strong>
        <ul>
          {Object.entries(reservation.rooms).map(([roomType, count]) => (
            <li key={roomType}>
              {roomType}: {count} {count > 1 ? t`rooms` : t`room`}
            </li>
          ))}
        </ul>
      </div>

      <div className="reservation-actions">
        <button className="download-btn" onClick={handleDownloadPDF}>
          {t`Download PDF`}
        </button>

        {reservation.status !== "CANCELED" && (
          <button className="cancel-btn" onClick={handleCancelReservation}>
            {t`Cancel Reservation`}
          </button>
        )}

        {reservation.paymentStatus !== "Paid" && !freeTickets.length && (
          <>
            <button
              className="add-new-btn"
              onClick={() => setIsPaying((prev) => !prev)}
            >
              {isPaying ? t`Cancel Payment` : t`Pay Remaining Amount`}
            </button>

            {isPaying && (
              <div className="payment-section">
                <label>
                  <input
                    type="checkbox"
                    checked={cardAmount > 0}
                    onChange={handleCardCheckboxChange}
                  />
                  {t`Card`}
                  {cardAmount > 0 && (
                    <input
                      type="number"
                      value={cardAmount}
                      onChange={(e) => setCardAmount(parseFloat(e.target.value) || 0)}
                      placeholder={t`Enter card amount`}
                      disabled={cashAmount > 0}
                    />
                  )}
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={cashAmount > 0}
                    onChange={handleCashCheckboxChange}
                  />
                  {t`Cash`}
                  {cashAmount > 0 && (
                    <input
                      type="number"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(parseFloat(e.target.value) || 0)}
                      placeholder={t`Enter cash amount`}
                      disabled={cardAmount > 0}
                    />
                  )}
                </label>

                <button onClick={handlePaymentSubmit}>
                  {t`Submit Payment`}
                </button>

                {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
              </div>
            )}
          </>
        )}

        {/* {freeTickets.length === 0 && reservation.status !== "CANCELED" && (
          <button
            className="download-btn"
            onClick={handleCreateFreeTickets}
            disabled={isGeneratingFreeTickets}
          >
            {isGeneratingFreeTickets ? t`Generating...` : t`Create Free Tickets`}
          </button>
        )} */}

        {/* {reservation.ticketIds && reservation.ticketIds.length > 0 && (
          <div>
            <h3>{t`Generated Tickets:`}</h3>
            {reservation.ticketIds.map((ticketId) => (
              <button
                key={ticketId}
                onClick={() => handleTicketDownload(ticketId)}
                className="download-btn"
              >
                {t`Download Ticket`} {ticketId}
              </button>
            ))}
          </div>
        )} */}
      </div>
    </div>
  );
};

export default ReservationCard;
